import type { RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Divider, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Text } from '@pbt/pbt-ui-components'

import ListItemWrapper from '~/components/dashboard/soapV2/order/ListItemWrapper'
import OrderTooltip from '~/components/dashboard/soapV2/order/OrderTooltip'
import ReadonlyOrderTooltip from '~/components/dashboard/soapV2/order/ReadonlyOrderTooltip'
import FeatureToggle from '~/constants/featureToggle'
import { editOrder } from '~/store/actions/orders'
import {
  useGetIsCheckedItem,
  useOrderCheckAllListItems,
  useOrderCheckItem,
  useOrderSelectedItems,
  useOrderUncheckItem,
} from '~/store/hooks/orders'
import {
  getFeatureToggle,
  getIsSetBundlePriceEnabled,
  getSearchCatalogFiltersAndFacetsEnabled,
} from '~/store/reducers/constants'
import { getAppointmentLineItemsMap } from '~/store/reducers/finance'
import { getSelectedOrders } from '~/store/reducers/orders'
import type {
  BaseChargeTabProps,
  Order,
  OrderCallbackWithTypeCheck,
  OrderSearchFilter,
} from '~/types'
import {
  findOrderItemByItem,
  getItemColor,
  hasPriceOrAggregatedPrice,
  isObsoleteGlobalItem,
} from '~/utils/orderUtils'
import useGetIsCatalogSelectableItem from '~/utils/useGetIsCatalogSelectableItem'

const useStyles = makeStyles(
  (theme) => ({
    lineItem: {
      '&:hover': {
        backgroundColor: theme.colors.soapListItemHover,
      },
    },
  }),
  { name: 'ChargeSearchResultItem' },
)

export interface ChargeSearchResultItemProps extends Omit<
  BaseChargeTabProps,
  'enableBackendFiltering'
> {
  clientId: string
  contentRef: RefObject<any>
  disabledWeakMap: WeakMap<Order, boolean>
  isFirstItem: boolean
  isFirstOccurrenceWithoutPrice: boolean
  isReceivingOrders: boolean
  onSelect: (itemId: string | undefined, meta?: Record<string, any>) => void
  order: Order
  patientId: string
  searchResultFilter?: OrderSearchFilter[]
  searchTerm: string
}

const ChargeSearchResultItem = ({
  clientId,
  contentRef,
  disabled,
  disabledWeakMap,
  handleEditListItem,
  handleRefill,
  handleSavePrescription,
  isFirstItem,
  isFirstOccurrenceWithoutPrice,
  isReceivingOrders,
  logItemStateGetter,
  openPrescriptionDialog,
  order,
  patientId,
  searchResultFilter,
  searchTerm,
  onSelect,
}: ChargeSearchResultItemProps) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { t } = useTranslation(['Search', 'Common'])

  const appointmentLineItemsMap = useSelector(getAppointmentLineItemsMap)
  const selectedOrders = useSelector(getSelectedOrders)

  const isCheckedItem = useGetIsCheckedItem()
  const {
    isSelectedItem,
    selectedSubItem,
    setSelectedSubItem,
    updateSelectedItem,
  } = useOrderSelectedItems()
  const onCheckItem = useOrderCheckItem({
    handleSavePrescription,
    logItemStateGetter,
    openPrescriptionDialog,
    onSelectItem: onSelect,
  })
  const handleCheckAllListItems = useOrderCheckAllListItems()
  const handleListItemUncheck = useOrderUncheckItem()

  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const isSearchCatalogFiltersAndFacetsEnabled = useSelector(
    getSearchCatalogFiltersAndFacetsEnabled,
  )

  const isNotObsolete = !isObsoleteGlobalItem(order, isFoodCatalogEnabled)
  const toolTipText = isNotObsolete ? (
    <ReadonlyOrderTooltip order={order} />
  ) : (
    t('Common:OBSOLETE')
  )

  const getIsCatalogSelectableItem = useGetIsCatalogSelectableItem()
  const isSetBundlePriceEnabled = useSelector(getIsSetBundlePriceEnabled)

  const isSelectableItem = getIsCatalogSelectableItem(order)

  const isFirstItemAndHasPrice =
    isFirstItem && hasPriceOrAggregatedPrice(order, isSetBundlePriceEnabled)
  const title = isFirstItemAndHasPrice
    ? t('Soap:SOAP_ORDERS_LOCKED.MY_LIST')
    : isFirstOccurrenceWithoutPrice
      ? t('Soap:SOAP_ORDERS_LOCKED.FULL_LIST')
      : undefined

  const onUncheckItem: OrderCallbackWithTypeCheck = (
    currentOrder,
    checkOptions,
  ) => {
    handleListItemUncheck(currentOrder, checkOptions)
  }

  const onCheckAll = (currentOrder: Order, checked: boolean) => {
    handleCheckAllListItems(currentOrder, checked)
  }

  const onUpdateItem = (newOrder: Order) => {
    const fullOrder = findOrderItemByItem(newOrder, selectedOrders)
    dispatch(editOrder({ ...fullOrder, ...newOrder }))
  }

  return (
    <Grid>
      {!isSearchCatalogFiltersAndFacetsEnabled && title && (
        <>
          <Text strong mb={0.5} ml={2} variant="body2">
            {title}
          </Text>
          <Grid mb={0.5}>
            <Divider />
          </Grid>
        </>
      )}
      <Grid className={classes.lineItem}>
        <ListItemWrapper
          includeOnHandQuantity
          includePrice
          showTooltip
          useChargesWidgetView
          ItemTooltipComponent={OrderTooltip}
          ListItemProps={{
            showListItemActions: true,
            onRefillSelect: handleRefill,
          }}
          clientId={clientId}
          disabledWeakMap={disabledWeakMap}
          editDisabled={disabled}
          getItemColor={getItemColor(
            appointmentLineItemsMap || {},
            selectedOrders,
          )}
          isCheckedItem={isCheckedItem}
          isDisabled={
            disabled || (disabledWeakMap && disabledWeakMap.get(order))
          }
          isLoading={isReceivingOrders}
          isSelectableItem={isSelectableItem}
          isSelected={isSelectedItem(order)}
          order={order}
          patientId={patientId}
          ref={contentRef}
          searchResultFilter={searchResultFilter}
          searchTerm={searchTerm}
          selectedSubItem={selectedSubItem}
          toolTipText={toolTipText}
          updateSelectedItem={updateSelectedItem}
          updateSelectedSubItem={setSelectedSubItem}
          onCheckAll={onCheckAll}
          onCheckItem={onCheckItem}
          onEditItem={handleEditListItem}
          onUncheckItem={onUncheckItem}
          onUpdateItem={onUpdateItem}
        />
      </Grid>
    </Grid>
  )
}

export default ChargeSearchResultItem
